import { ENV_VARS } from 'constants/environment.constants';
import { kf1AuthenticatedHttpClient } from 'kfone-frontend-core';
import {
  IAllGapsCategoryResponse,
  IAllPayGapsResponse,
  ICompensationResponse,
  IHeadcountDistributionResponse,
  IPayByGroupResponse,
  IReportMetaDataResponse,
} from 'model/interfaces';
import {
  AllGapsCategoryURLParams,
  AllPayGapsURLParams,
  CompensationURLParams,
  DownloadExcelReportParams,
  HeadcountDistributionURLParams,
  PayByGroupURLParams,
  ReportMetaDataURLParams,
} from 'model/types';
import { useTranslation } from 'react-i18next';
import { useReportDeailStore } from 'store';
import { getCompensationName } from 'utilities/base-elements-translation';

export const useOrganizationDataService = () => {
  const { t } = useTranslation();
  const {
    updateReportMetaDataLoading,
    updateReportMetaData,
    updateHeadcountDistributionLoading,
    updateHeadcountDistributionData,
    updatePayByGroupLoading,
    updatePayByGroupData,
    updateAllPayGapsLoading,
    updateAllPayGapsData,
    updateQuartileCategoryLoading,
    updateQuartileCategoryData,
    updateCompTypeOptions,
    updateCompType,
    updateIsReportDetailsLoading,
  } = useReportDeailStore();

  const fetchReportMetaData = (params: ReportMetaDataURLParams) => {
    const config = {
      baseURL: ENV_VARS.API_URL,
      url: 'v2/elements/reportmetadata',
      method: 'GET',
      params,
    };
    kf1AuthenticatedHttpClient<IReportMetaDataResponse>(config).then(
      (response) => {
        if (response && response.data) {
          const { responseCode, data } = response.data;
          if (responseCode === 200 && data && Object.keys(data).length > 0) {
            updateReportMetaData(data);
          }
        }
        updateReportMetaDataLoading(false);
        updateIsReportDetailsLoading(false);
      },
      (error) => {
        updateReportMetaDataLoading(false);
        updateIsReportDetailsLoading(false);
      },
    );
  };

  const fetchCompensationData = (params: CompensationURLParams) => {
    const config = {
      baseURL: ENV_VARS.API_URL,
      url: 'v2/elements/compensation',
      method: 'GET',
      params,
    };
    kf1AuthenticatedHttpClient<ICompensationResponse>(config).then(
      (response) => {
        if (response && response.data) {
          const { responseCode, data } = response.data;
          if (responseCode === 200 && data && data.length > 0) {
            const options = data
              .filter((item) => item.activeFlag === true)
              .map((item) => ({
                label: getCompensationName(item.compElementLabel, t),
                value: item.compElementId,
              }));
            if (options.length > 0) {
              updateCompTypeOptions(options);
              updateCompType(options[0]);
            }
          }
        }
      },
      (error) => {
        console.error('Failed to fetch compensation data', error);
      },
    );
  };

  const fetchHeadcountDistribution = (params: HeadcountDistributionURLParams) => {
    updateHeadcountDistributionLoading(true);
    const config = {
      baseURL: ENV_VARS.API_URL,
      url: 'overview/headcountdistribution',
      method: 'GET',
      params,
    };
    kf1AuthenticatedHttpClient<IHeadcountDistributionResponse>(config).then(
      (response) => {
        if (response && response.data) {
          const { responseCode, data } = response.data;
          if (responseCode === 200 && data.length > 0) {
            updateHeadcountDistributionData(data);
          }
        }
        updateHeadcountDistributionLoading(false);
      },
      (error) => {
        updateHeadcountDistributionLoading(false);
      },
    );
  };

  const fetchPayByGroup = (params: PayByGroupURLParams) => {
    updatePayByGroupLoading(true);
    const config = {
      baseURL: ENV_VARS.API_URL,
      url: 'v2/overview/averagemedianpay',
      method: 'GET',
      params,
    };
    kf1AuthenticatedHttpClient<IPayByGroupResponse>(config).then(
      (response) => {
        if (response && response.data) {
          const { responseCode, data } = response.data;
          if (responseCode === 200 && data.length > 0) {
            updatePayByGroupData(data);
          }
        }
        updatePayByGroupLoading(false);
      },
      (error) => {
        updatePayByGroupLoading(false);
      },
    );
  };

  const fetchAllPayGaps = (params: AllPayGapsURLParams) => {
    updateAllPayGapsLoading(true);
    const config = {
      baseURL: ENV_VARS.API_URL,
      url: 'v2/overview/allpaygaps',
      method: 'GET',
      params,
    };
    kf1AuthenticatedHttpClient<IAllPayGapsResponse>(config).then(
      (response) => {
        if (response && response.data) {
          const { responseCode, data } = response.data;
          if (responseCode === 200 && data.length > 0) {
            updateAllPayGapsData(data);
          }
        }
        updateAllPayGapsLoading(false);
      },
      (error) => {
        updateAllPayGapsLoading(false);
      },
    );
  };

  const fetchQuartileCategory = (params: AllGapsCategoryURLParams) => {
    updateQuartileCategoryLoading(true);
    const config = {
      baseURL: ENV_VARS.API_URL,
      url: 'v2/paygaps/distributiondetails',
      method: 'GET',
      params,
    };
    kf1AuthenticatedHttpClient<IAllGapsCategoryResponse>(config).then(
      (response) => {
        const { responseCode, data } = response.data;
        if (responseCode === 200 && data && data.length > 0) {
          updateQuartileCategoryData(data);
        } else {
          updateQuartileCategoryData([]);
        }
        updateQuartileCategoryLoading(false);
      },
      (error) => {
        updateQuartileCategoryData([]);
        updateQuartileCategoryLoading(false);
      },
    );
  };

  const fetchDownloadExcelReport = async (params: DownloadExcelReportParams) => {
    return (
      await kf1AuthenticatedHttpClient({
        baseURL: ENV_VARS.API_URL,
        url: 'v2/report/analysis/export',
        method: 'POST',
        data: { ...params },
        responseType: 'blob',
      })
    ).data;
  };

  return {
    fetchReportMetaData,
    fetchCompensationData,
    fetchHeadcountDistribution,
    fetchPayByGroup,
    fetchAllPayGaps,
    fetchQuartileCategory,
    fetchDownloadExcelReport,
  };
};
